import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { CreateBadgeAction, BadgeListAction } from "../../Redux/Actions/RewardAction";
import { useS3Upload } from "../../hooks/useS3Upload";
import { ApiConfig } from "../../Config/CommonConfig";

interface CreateBadgeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void; 
}

const CreateBadgeModal: React.FC<CreateBadgeModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch<any>();
  const {
    uploadFile,
    isLoading: s3Loading,
    s3Path,
    success: uploadSuccess,
    error: uploadError,
    resetState: resetS3State,
  } = useS3Upload();

  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    description: "",
    level: "",
    criteria_type: "streak",
    criteria_value: "",
    earning_criteria: "",
    summary: "",
  });

  useEffect(() => {
    if (!open) {
      resetS3State();
      setImageFile(null);
      setImagePreview("");
    }
  }, [open, resetS3State]);

  useEffect(() => {
    if (uploadSuccess && s3Path) {
      createBadge(s3Path);
    }
  }, [uploadSuccess, s3Path]);

  useEffect(() => {
    if (uploadError && imageFile) {
      setLoading(false);
    }
  }, [uploadError, imageFile]);

  const handleChange = (e: any) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files && e?.target?.files[0]) {
      const file = e?.target?.files[0];
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (imageFile) {
        uploadFile(ApiConfig?.BadgeUploadPresigned, imageFile, imageFile?.type);
      } else {
        createBadge("");
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const createBadge = async (imageUrl: string) => {
    try {
      let levelNumber = null;
      if (formData?.level?.toLowerCase() === "beginner") levelNumber = 1;
      if (formData?.level?.toLowerCase() === "intermediate") levelNumber = 2;
      if (formData?.level?.toLowerCase() === "advanced") levelNumber = 3;

      const badgeData = {
        code: formData?.code,
        name: formData?.name,
        description: formData?.description,
        summary: formData?.summary,
        level: levelNumber,
        image_url: imageUrl,
        criteria_type: formData?.criteria_type,
        criteria_json: {
          criteria_type: formData?.criteria_type,
          value: Number(formData?.criteria_value),
        },
        earning_criteria: formData?.earning_criteria,
        is_active: true,
        is_expire: false,
      };

      const result = await dispatch(CreateBadgeAction(badgeData));
      
      if (result && result.status === true) {
        resetForm();
        onClose();
        if (onSuccess) {
          onSuccess(); 
        }
        const param='?page=1&page_size=10';
        dispatch(BadgeListAction(param));
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: "",
      name: "",
      description: "",
      level: "",
      criteria_type: "streak",
      criteria_value: "",
      earning_criteria: "",
      summary: "",
    });
    setImageFile(null);
    setImagePreview("");
    setLoading(false);
    resetS3State();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Badge</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <TextField
            name="code"
            label="Badge Code"
            value={formData?.code}
            onChange={handleChange}
            fullWidth
            required
            placeholder="e.g., BADGE_2025_COURSE"
          />

          <TextField
            name="name"
            label="Badge Name"
            value={formData?.name}
            onChange={handleChange}
            fullWidth
            required
            placeholder="e.g., Course Completion Badge"
          />

          <TextField
            name="description"
            label="Description"
            value={formData?.description}
            onChange={handleChange}
            fullWidth
            multiline
            rows={3}
            placeholder="Describe what this badge represents"
          />

          <FormControl fullWidth>
            <InputLabel>Level</InputLabel>
            <Select
              name="level"
              value={formData?.level}
              onChange={handleChange}
              label="Level"
              required
            >
              <MenuItem value="Beginner">Beginner</MenuItem>
              <MenuItem value="Intermediate">Intermediate</MenuItem>
              <MenuItem value="Advanced">Advanced</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Criteria Type</InputLabel>
            <Select
              name="criteria_type"
              value={formData?.criteria_type}
              onChange={handleChange}
              label="Criteria Type"
            >
              <MenuItem value="streak">Streak</MenuItem>
              <MenuItem value="course">Course</MenuItem>
              <MenuItem value="assessment">Assessment</MenuItem>
            </Select>
          </FormControl>

          <TextField
            name="criteria_value"
            label="Criteria Value"
            value={formData?.criteria_value}
            onChange={handleChange}
            fullWidth
            type="number"
            required
            placeholder="e.g., 100"
            helperText="Number of streaks/courses/assessments required"
          />

          <TextField
            name="earning_criteria"
            label="Earning Criteria Description"
            value={formData?.earning_criteria}
            onChange={handleChange}
            fullWidth
            placeholder="e.g., Complete 7 day streak"
            helperText="Human-readable description of how to earn this badge"
          />

          <TextField
            name="summary"
            label="Summary"
            value={formData?.summary}
            onChange={handleChange}
            fullWidth
            multiline
            rows={2}
            placeholder="Brief summary of the badge"
          />

          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Upload Badge Image *
            </Typography>

            {imagePreview && (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={imagePreview}
                  alt="Badge preview"
                  style={{
                    maxWidth: 120,
                    maxHeight: 120,
                    objectFit: "contain",
                    borderRadius: 4,
                  }}
                />
              </Box>
            )}

            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ width: "100%" }}
              required
            />
            {imageFile && (
              <Typography
                variant="caption"
                sx={{ mt: 1, display: "block", color: "#0555c8" }}
              >
                Selected: {imageFile?.name}
              </Typography>
            )}
            {uploadError && imageFile && (
              <Typography
                variant="caption"
                sx={{ mt: 1, display: "block", color: "error.main" }}
              >
                Upload Error: {uploadError}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading || s3Loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            s3Loading ||
            !formData?.code ||
            !formData?.name ||
            !formData?.level ||
            !formData?.criteria_value ||
            !imageFile
          }
          sx={{
            backgroundColor: "#0555c8",
            "&:hover": {
              backgroundColor: "#043c99",
            },
          }}
        >
          {loading || s3Loading ? (
            <CircularProgress size={24} />
          ) : (
            "Create Badge"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateBadgeModal;
